import { Button } from '@/components/ui/button';
import { usePreboardingConflictSummary } from '@/hooks/use-preboarding-conflicts';
import { usePermission } from '@/lib/permissions';
import { AlertTriangle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { PreboardingConflictModal } from './preboarding-conflict-modal';

interface PreboardingConflictBannerProps {
  employeeId: string;
  /** Open the review modal automatically (e.g. from a notification deep link). */
  autoOpen?: boolean;
}

// Shown on the employee profile when a pre-boarding submission staged values
// that differ from the existing profile data. Gated on forms.manage so only HR
// sees it; the GET would 403 for anyone else.
export function PreboardingConflictBanner({
  employeeId,
  autoOpen = false,
}: PreboardingConflictBannerProps) {
  const canManage = usePermission('forms.manage');
  const { data: summary } = usePreboardingConflictSummary(employeeId, canManage);
  const pendingCount = summary?.pendingCount ?? 0;
  const [open, setOpen] = useState(false);
  const [autoOpened, setAutoOpened] = useState(false);

  useEffect(() => {
    if (autoOpen && !autoOpened && pendingCount > 0) {
      setOpen(true);
      setAutoOpened(true);
    }
  }, [autoOpen, autoOpened, pendingCount]);

  if (!canManage) return null;

  return (
    <>
      {pendingCount > 0 && (
        <div className="mb-5 flex items-center justify-between gap-3 rounded-lg border border-amber-300/60 bg-amber-50 px-4 py-3 dark:border-amber-500/30 dark:bg-amber-500/10">
          <div className="flex items-center gap-2.5">
            <AlertTriangle className="h-5 w-5 flex-shrink-0 text-amber-600 dark:text-amber-400" />
            <p className="text-sm text-amber-900 dark:text-amber-100">
              A pre-boarding submission has{' '}
              <strong>
                {pendingCount} field{pendingCount > 1 ? 's' : ''}
              </strong>{' '}
              that differ from this profile and need your review.
            </p>
          </div>
          <Button
            size="sm"
            className="flex-shrink-0 bg-amber-600 text-white hover:bg-amber-600/90"
            onClick={() => setOpen(true)}
          >
            Review
          </Button>
        </div>
      )}
      <PreboardingConflictModal employeeId={employeeId} open={open} onOpenChange={setOpen} />
    </>
  );
}
